import { forwardRef, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Box, IconButton, Stack, Tooltip, useTheme } from '@mui/material';
import { GridToolbarQuickFilter } from '@mui/x-data-grid-pro';

import { AgencyApi, UserApi } from '@/api';
import { DataGrid } from '@/components';
import {
  DataGridToolbar,
  DataGridToolbarProps,
} from '@/components/DataGrid/DataGridToolbar';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import { toolbarStyles } from '@/components/DataGrid/styles';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { AgencyType, IAgency, IUser } from '@/models';
import { IDevice } from '@/models/device';
import { employeeDeviceDataGridCols } from '@/pages/Employees/data';
import { useToastStore, useUserStore, ConfirmTypes } from '@/store';
import { getLocalStorageItem } from '@/utils';

// Extended user interface to handle API response property
interface EmployeeWithDeviceRestriction extends IUser {
  isLockedFromNewDevices?: boolean;
}

interface EmployeeDevicesDataGridProps {
  employee?: EmployeeWithDeviceRestriction;
  refreshEmployee: () => Promise<void>;
}

// Custom toolbar that adds the lock button on the left side
const CustomToolbar = forwardRef<
  HTMLDivElement,
  DataGridToolbarProps & { lockButton?: React.ReactNode }
>(function CustomToolbar(
  { lockButton, cta, hideFilter, hideQuickFilter, ...rest },
  ref,
) {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      padding="12px"
      sx={toolbarStyles(theme).GridFilterPanel}
      ref={ref}
      {...rest}
    >
      <Stack spacing="4px" direction="row" alignItems="center">
        <Box ml={1}>
          <DataGridToolbar
            {...rest}
            hideFilter
            hideQuickFilter={true}
            sx={{ padding: 0, minHeight: 'auto' }}
          />
        </Box>
        {lockButton}
      </Stack>
      <Stack
        spacing="12px"
        direction="row"
        justifyContent="center"
        alignItems="center"
      >
        {!hideQuickFilter && (
          <GridToolbarQuickFilter
            sx={toolbarStyles(theme).GridToolbarQuickFilter}
            variant="outlined"
            debounceMs={800}
          />
        )}
        {cta}
      </Stack>
    </Stack>
  );
});

export const EmployeeDevicesDataGrid = ({
  employee: currentEmployee,
  refreshEmployee,
}: EmployeeDevicesDataGridProps) => {
  const { employeeId, agencyId } = useParams();
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const [currentAgency, setCurrentAgency] = useState<IAgency>();
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `devices-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return employeeDeviceDataGridCols;
      }

      return mergeColumnSettings(employeeDeviceDataGridCols, savedSettings);
    },
  );

  const fetchAgencyData = async () => {
    try {
      if (!agencyId) return;
      const res = await AgencyApi.getAgency(agencyId);
      setCurrentAgency(res.data);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  useEffect(() => {
    fetchAgencyData();
  }, [agencyId]);

  useEffect(() => {
    if (currentAgency && currentAgency.type !== AgencyType.POLICE) {
      setColumnSettings((prevSettings) => {
        return prevSettings.filter((col) => col.field !== 'isNCICEnabled');
      });
    }
  }, [currentAgency, columnSettings]);

  const columns = useDataGridColumns({
    type: 'employeeDevices',
    columnSettings,
  });

  const fetchDevices = useCallback(
    async (
      page = 1,
      limit = 100,
      filter?: string,
    ): Promise<{
      rows: IDevice[];
      totalCount: number;
    }> => {
      try {
        if (!employeeId) {
          return { rows: [], totalCount: 0 };
        }

        // Prepare parameters for the API call
        const params: any = {
          page,
          limit,
        };

        // Add filter if provided
        if (filter) {
          params.filter = filter;
        }

        const response = await UserApi.getUserDevices(employeeId, params);

        // Normalize the data to ensure each row has a proper _id
        const normalizedRows = response.data.results.map((device: any) => {
          // If it's a Mongoose document with _doc property, use that data
          if (device._doc) {
            return {
              ...device._doc,
              lastActive: device.lastActive,
            };
          }
          // Otherwise return the device as is
          return device;
        });

        return {
          rows: normalizedRows,
          totalCount: response.data.count,
        };
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return { rows: [], totalCount: 0 };
      }
    },
    [updateToast, employeeId, refreshKey],
  );

  const handleToggleNewDeviceRestriction = async () => {
    if (!employeeId || !currentEmployee) return;
    try {
      setIsLoading(true);

      // Use either property name based on what's available
      const isRestricted =
        currentEmployee.isLockedFromNewDevices ??
        currentEmployee.isNewDevicesRestricted;

      const newRestrictionState = !isRestricted;

      await UserApi.updateDeviceRestriction(employeeId, newRestrictionState);

      await refreshEmployee();

      // Trigger a refresh of the DataGrid
      setRefreshKey((prev) => prev + 1);

      updateToast({
        open: true,
        message: newRestrictionState
          ? 'User is now restricted from signing in from new devices'
          : 'User can now sign in from new devices',
        type: ConfirmTypes.SUCCESS,
      });
    } catch (err: any) {
      updateToast({
        open: true,
        message: err.message,
        type: ConfirmTypes.ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get the restriction state using either property name
  const isDeviceRestricted =
    currentEmployee?.isLockedFromNewDevices ??
    currentEmployee?.isNewDevicesRestricted;

  // Make the lock button look like a standard button with a box around it
  const lockButton = (
    <Tooltip
      title={
        isDeviceRestricted
          ? 'This employee is locked from adding new devices'
          : 'Click to lock this employee from adding new devices'
      }
    >
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.25,
          p: 0.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
          height: '36px',
          width: '36px',
        }}
      >
        <IconButton
          onClick={handleToggleNewDeviceRestriction}
          disabled={isLoading}
          color={isDeviceRestricted ? 'error' : 'default'}
          aria-label="toggle new device restriction"
          size="small"
          sx={{ minWidth: 0, padding: 0 }}
        >
          {isDeviceRestricted ? (
            <LockIcon fontSize="small" />
          ) : (
            <LockOpenIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Tooltip>
  );

  return (
    <Box height="100%" paddingTop="14px">
      <DataGrid
        name="employeeDevices"
        key={refreshKey}
        fetchData={fetchDevices}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        columns={columns}
        hideFilter
        filterMode="client"
        getRowId={(row) => row._id || (row._doc && row._doc._id)}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            lockButton,
          } as any, // Use type assertion to bypass type checking for custom props
        }}
      />
    </Box>
  );
};
